import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ("buyer" | "seller" | "admin")[];
  /**
   * Identity verification (KYC) is OPTIONAL platform-wide. It is only enforced
   * server-side when a seller requests a payout. Set this to true only if a
   * route should explicitly require a verified identity.
   */
  requireKyc?: boolean;
}

const ProtectedRoute = ({ children, allowedRoles, requireKyc = false }: ProtectedRouteProps) => {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Role check
  if (allowedRoles && profile && !allowedRoles.includes(profile.role)) {
    const redirectPath = profile.role === "admin" ? "/admin/dashboard" : `/dashboard/${profile.role}`;
    return <Navigate to={redirectPath} replace />;
  }

  // Optional KYC: only enforced when a route explicitly opts in via requireKyc.
  if (
    requireKyc &&
    profile &&
    profile.role !== "admin" &&
    profile.kyc_status !== "verified" &&
    location.pathname !== "/kyc"
  ) {
    return <Navigate to="/kyc" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
